

import React, { useEffect, useState, useMemo } from 'react';
import './Management3.css';
import axios from 'axios';
import Cashbilling from './Cashbilling';
import "./Company.css";
import "./Style.css";
import Settlement from './Settlement';
import WalletAdd from './WalletAdd';
import { useLoader } from "../../../../context/LoaderProvider.jsx";
import { Approval } from '@mui/icons-material';

const Management3 = () => {
  const [activeTab, setActiveTab] = useState('Cash Billing');
  const [vendorId, setVendorId] = useState(null);
  const isMobile = useState(window.innerWidth < 768);
  const [selectedDate, setSelectedDate] = useState("");
  const [allOrders, setAllOrders] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const { showLoader, hideLoader } = useLoader();

  // Category filter states
  const [categories, setCategories] = useState(["All Categories"]);
  const [selectedCategory, setSelectedCategory] = useState("All Categories");
  const [dishCategoryMap, setDishCategoryMap] = useState({});
  const containerHeight = window.innerWidth < 768
    ? '300px'       // Mobile
    : window.innerWidth < 850
      ? '300px'     // Below 850px
      : window.innerWidth < 1400
        ? '410px'   // Between 850px and 1399px
        : window.innerWidth < 1600
          ? '430px' // Between 1400px and 1599px
          : '450px'; // Above 1600px

  // Helper function to determine if order is completed
  const isOrderCompleted = (order) => {
    return order.status?.toLowerCase() === "completed" ||
      order.cashStatus?.toLowerCase() === "accept" ||
      order.paymentStatus?.toLowerCase() === "success";
  };

  // Calculate totals based on filtered data (date-specific)
  const totals = useMemo(() => {
    let walletIncome = 0, onlineIncome = 0, cashIncome = 0;

    filteredData.forEach(order => {
      const amount = order.grandTotal || 0;
      const method = order.paymentMethod?.toLowerCase();

      // Check if order is completed
      if (isOrderCompleted(order)) {
        if (method === "maamaas_wallet" || method === "wallet") {
          walletIncome += amount;
        } else if (method === "online_payment" || method === "online" || method === "online_payment") {
          onlineIncome += amount;
        } else if (method === "cash") {
          cashIncome += amount;
        }
      }
    });

    const totalIncome = walletIncome + onlineIncome + cashIncome;

    return {
      walletIncome,
      onlineIncome,
      cashIncome,
      totalIncome
    };
  }, [filteredData]);

  // Date navigation functions
  const goToPreviousDay = () => {
    if (selectedDate) {
      const currentDate = new Date(selectedDate);
      currentDate.setDate(currentDate.getDate() - 1);
      setSelectedDate(currentDate.toISOString().split('T')[0]);
    } else {
      // If no date selected, start from yesterday
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      setSelectedDate(yesterday.toISOString().split('T')[0]);
    }
  };

  const goToNextDay = () => {
    if (selectedDate) {
      const currentDate = new Date(selectedDate);
      currentDate.setDate(currentDate.getDate() + 1);

      // Don't allow future dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (currentDate <= today) {
        setSelectedDate(currentDate.toISOString().split('T')[0]);
      }
    } else {
      // If no date selected, start from today
      const today = new Date();
      setSelectedDate(today.toISOString().split('T')[0]);
    }
  };

  const goToToday = () => {
    const today = new Date();
    setSelectedDate(today.toISOString().split('T')[0]);
  };

  // Fetch orders
  useEffect(() => {
    if (!vendorId) return;
    const fetchOrders = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }

      try {
        showLoader();
        const res = await axios.get(
          `https://backend.maamaas.com/food/api/orders/vendor/${vendorId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const orders = res.data || [];
        setAllOrders(orders);
        setFilteredData(orders);

        // Debug: Check order dates
        //console.log("Orders fetched:", orders.length);
        orders.forEach(order => {
          //console.log("Order date:", order.orderDateAndTime, "Formatted:", new Date(order.orderDateAndTime).toISOString().split('T')[0]);
        });

      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        hideLoader();
      }
    };

    fetchOrders();
  }, [vendorId]);

  // Fetch categories and create dish-to-category mapping
  useEffect(() => {
    if (!vendorId) return;

    const fetchCategoriesAndMapping = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        console.error("Authorization token is missing");
        return;
      }

      try {
        showLoader();
        const res = await axios.get(
          `https://backend.maamaas.com/food/api/dishes/vendor/${vendorId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const dishes = res.data || [];

        // Create mapping of dishName to category name
        const categoryMap = {};
        const categoriesSet = new Set();

        dishes.forEach(dish => {
          if (dish.parentId === 0) {
            // This is a category
            categoriesSet.add(dish.dishName);
          } else {
            // This is a dish - find its category
            const category = dishes.find(cat => cat.dishId === dish.parentId);
            if (category) {
              categoryMap[dish.dishName] = category.dishName;
            }
          }
        });

        //console.log("Category mapping created:", categoryMap);
        //console.log("Categories found:", Array.from(categoriesSet));

        setDishCategoryMap(categoryMap);
        setCategories(["All Categories", ...Array.from(categoriesSet).sort()]);

      } catch (err) {
        console.error("Error fetching categories:", err);
      } finally {
        hideLoader();
      }
    };

    fetchCategoriesAndMapping();
  }, [vendorId]);

  // Filter orders based on selected date AND selected category - FIXED
  useEffect(() => {
    if (!selectedDate && selectedCategory === "All Categories") {
      setFilteredData(allOrders);
      return;
    }

    const filtered = allOrders.filter((order) => {
      // Date filter - FIXED
      let dateMatch = true;
      if (selectedDate) {
        try {
          const orderDate = new Date(order.orderDateAndTime);
          const orderDateStr = orderDate.toISOString().split('T')[0];
          dateMatch = orderDateStr === selectedDate;
        } catch (error) {
          console.error("Error parsing date:", order.orderDateAndTime);
          dateMatch = false;
        }
      }

      // Category filter - FIXED
      let categoryMatch = true;
      if (selectedCategory !== "All Categories") {
        categoryMatch = order.order && order.order.some((item) => {
          const dishCategory = dishCategoryMap[item.dishName];
          if (!dishCategory) return false;
          return dishCategory.toLowerCase().includes(selectedCategory.toLowerCase());
        });
      }

      return dateMatch && categoryMatch;
    });



    setFilteredData(filtered);
  }, [selectedDate, selectedCategory, allOrders, dishCategoryMap]);

  useEffect(() => {
    const storedVendorId = localStorage.getItem("vendorId");
    if (storedVendorId) {
      setVendorId(storedVendorId);
    }
  }, []);

  // Other helper functions for data display
  const getUserName = (order) => {
    return order.userName || "Guest";
  };

  const getTransactionId = (order) => {
    const paymentMethod = order.paymentMethod?.toLowerCase();
    if (paymentMethod === "cash") {
      return "-";
    }
    return order.transactionId || "-";
  };

  const getOrderStatus = (order) => {
    return order.cashStatus || order.status || order.paymentStatus || "Pending";
  };

  const getPaymentMethod = (order) => {
    const method = order.paymentMethod;
    if (!method) return "Unknown";

    switch (method.toLowerCase()) {
      case "online_payment":
        return "Online Payment";
      case "maamaas_wallet":
        return "Wallet";
      case "cash":
        return "Cash";
      default:
        return method;
    }
  };

  const exportToCSV = () => {
    if (filteredData.length === 0) {
      alert("No data to export!");
      return;
    }

    const headers = [
      "S.No",
      "Order ID",
      "Date",
      "Category",
      "Type",
      "Name",
      "Payment",
      "Order Status",
      "Amount",
    ];

    const rows = filteredData.map((item, index) => [
      index + 1,
      item.orderId,
      new Date(item.orderDateAndTime).toLocaleDateString("en-GB"),
      item.appType || "-",
      item.orderType || item.orderService || "-",
      getUserName(item),
      getPaymentMethod(item),
      getOrderStatus(item),
      Math.round(item.grandTotal || 0),
    ]);

    const csvContent =
      [headers, ...rows]
        .map((row) => row.map((v) => `"${v}"`).join(","))
        .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `orders_${selectedDate || new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const clearDateFilter = () => {
    setSelectedDate("");
    setSelectedCategory("All Categories");
  };

  const clearCategoryFilter = () => {
    setSelectedCategory("All Categories");
  };

  // Format date for display
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Overview Tab Content
  const tabContent = {
    Overview: (
      <div style={{ marginTop: "20px" }}>
        <div className="status-overview">
          {[
            { title: "Total Income", value: totals.totalIncome, color: "#2c6eaa" },
            { title: "Wallet Income", value: totals.walletIncome, color: "#4ecdc4" },
            { title: "Online Income", value: totals.onlineIncome, color: "#45b7d1" },
            { title: "Cash Income", value: totals.cashIncome, color: "#96ceb4" },
          ].map((card, idx) => (
            <div key={idx} style={{ background: card.color, color: "#fff" }} className="status-card dine-in">
              <h4 style={{ display: 'flex', justifyContent: "center", fontSize: "16px", margin: "10px 0" }}>
                {card.title}
              </h4>
              <p style={{
                fontWeight: "bold",
                display: 'flex',
                justifyContent: "center",
                fontSize: "18px",
                margin: "10px 0"
              }}>
                ₹{card.value.toLocaleString("en-IN")}
              </p>
            </div>
          ))}
        </div>

        <div className="payment-history-container">
          <style>
            {`
.payment-history-container {
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
}

.filters {
  margin-bottom: 20px;
  width: 100%;
}

.filters-single-line {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 15px;
  flex-wrap: nowrap;
  width: 100%;
}

.date-navigation {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.date-navigation button {
  padding: 8px 12px;
  background-color: #b15dc6;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  font-weight: bold;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
}

.date-navigation button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.today-button {
  background-color: #28a745 !important;
  padding: 6px 12px !important;
  font-size: 12px !important;
}

.clear-button {
  background-color: #ff6b6b !important;
  color: white !important;
  padding: 6px 12px !important;
  font-size: 12px !important;
  transition: opacity 0.2s ease;
}

.calendar-container {
  position: relative;
  display: inline-block;
}

.calendar-button {
  padding: 8px 16px;
  background-color: #b15cd6;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  min-width: 110px;
  justify-content: center;
}

.category-search {
  flex: 0 0 auto;
}

.category-search input {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 5px;
  background-color: white;
  font-size: 14px;
  min-width: 180px;
  outline: none;
}

.category-search input:focus {
  border-color: #b15cd6;
  box-shadow: 0 0 0 2px rgba(177, 92, 214, 0.2);
}

.export-button {
  padding: 10px 20px;
  background-color: #b15cd6;
  color: #fff;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  cursor: pointer;
  white-space: nowrap;
  flex: 0 0 auto;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  background: #fff;
  border-radius: 8px;
}

table {
  width: 100%;
  min-width: 850px;
  border-collapse: collapse;
}

th, td {
  border: 1px solid #ddd;
  padding: 10px;
  font-size: 13px;
  text-align: left;
  white-space: nowrap;
}

th {
  background-color: #b15dc6;
  font-weight: bold;
  font-size: 14px;
  color: white;
}

tr:nth-child(even) {
  background-color: #ffffff;
}

tr:nth-child(odd) {
  background-color: #f9f9f9;
}

@media screen and (max-width: 768px) {
  .payment-history-container {
    max-width: 100%;
    padding: 0px;
  }

  .filters-single-line {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .date-navigation {
    justify-content: center;
    width: 100%;
    flex-wrap: wrap;
  }

  .category-search {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .category-search input {
    width: 100%;
    max-width: 200px;
  }

  .export-button {
    width: 100%;
    max-width: 200px;
    align-self: center;
  }

  table {
    min-width: 600px;
  }
}

.my-container {
  overflow-y: scroll;
  -ms-overflow-style: none; 
  scrollbar-width: none;
}

.my-container::-webkit-scrollbar {
  display: none;
}

.tab-content {
  width: 100%;
  max-width: 840px;
}

@media (min-width: 999px) {
  .export-button {
    margin-left: 0 !important;
    margin-top: 0 !important;
  }
}
`}
          </style>

          <div className="filters"
            style={{ overflow: "auto", scrollbarWidth: "none", overflowY: "hidden" }}>
            <div className="filters-single-line">
              {/* Date Navigation */}
              <div className="date-navigation">
                <button onClick={goToPreviousDay} title="Previous Day">←</button>

                <div className="calendar-container">
                  <button
                    className="calendar-button"
                    onClick={() => document.getElementById("hiddenDateInput").showPicker()}
                    style={{ width: "12rem" }}
                  >
                    📅 {selectedDate ? formatDisplayDate(selectedDate) : "Select Date"}
                  </button>
                  <input
                    id="hiddenDateInput"
                    type="date"
                    max={new Date().toISOString().split("T")[0]}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    style={{
                      position: "absolute",
                      opacity: 0,
                      pointerEvents: "none",
                      width: "0px",
                      height: "0px",
                      left: "-30px",
                    }}
                  />
                </div>

                <button
                  onClick={goToNextDay}
                  disabled={selectedDate === new Date().toISOString().split('T')[0]}
                  title={selectedDate === new Date().toISOString().split('T')[0] ? "Can't go beyond today" : "Next Day"}
                >
                  →
                </button>

                <button onClick={goToToday} className="today-button" title="Go to Today">
                  Today
                </button>

                <button
                  onClick={clearDateFilter}
                  className="clear-button"
                  style={{
                    visibility: selectedDate ? 'visible' : 'hidden',
                    opacity: selectedDate ? 1 : 0,
                  }}
                >
                  Show All
                </button>
              </div>

              {/* Category Search Bar */}
              <div className="category-search">
                <input
                  type="text"
                  placeholder="Search by category..."
                  value={selectedCategory === "All Categories" ? "" : selectedCategory}
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    if (value === "") {
                      setSelectedCategory("All Categories");
                    } else {
                      setSelectedCategory(value);
                    }
                  }}
                  style={{
                    padding: "8px 12px",
                    border: "1px solid #ddd",
                    borderRadius: "5px",
                    fontSize: "14px",
                    minWidth: "180px",
                    outline: "none"
                  }}
                />
              </div>

              {/* Export Button */}
              <button className="export-button" onClick={exportToCSV}>
                Export
              </button>
            </div>
          </div>

          <div className="table-wrapper"
            style={{ marginTop: "-30px", }}>
            <table>
              <thead>
                <tr>
                  <th>S.No</th>
                  <th>OrderID</th>
                  <th>Transaction ID</th>
                  <th>Date & Time</th>
                  <th>Name</th>
                  <th>Payment</th>
                  <th>Status</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center" }}>
                      {selectedDate || selectedCategory !== "All Categories"
                        ? `No orders found for ${selectedDate ? formatDisplayDate(selectedDate) : ''}${selectedDate && selectedCategory !== "All Categories" ? ' and ' : ''}${selectedCategory !== "All Categories" ? `category: ${selectedCategory}` : ''}`
                        : 'No orders available'}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td>{item.orderId}</td>
                      <td>{getTransactionId(item)}</td>
                      <td>{new Date(item.orderDateAndTime).toLocaleString()}</td>
                      <td>{getUserName(item)}</td>
                      <td>{getPaymentMethod(item)}</td>
                      <td>{getOrderStatus(item)}</td>
                      <td>₹{Math.round(item.grandTotal || 0)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    ),
    "Cash Billing": <Cashbilling />,
    Settlement: <Settlement />,
    WalletApproval: <WalletAdd />,
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-lg-12 col-md-12 col-sm-12">
          <div className="left-column">
            <div className="tabs-wrapperpayment">
              <div style={{ textAlign: 'center' }}>
                {Object.keys(tabContent).map((label) => (
                  <button
                    key={label}
                    style={{
                      backgroundColor: activeTab === label ? '#b15dc6' : '#d9d9d9',
                      border: 'none',
                      borderRadius: '10px',
                      width: '110px',
                      height: '40px',
                      color: 'black',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      transition: 'background-color 0.3s ease',
                      margin: '10px'
                    }}
                    className={`overviewbuttons ${activeTab === label ? "active" : ""}`}
                    onClick={() => setActiveTab(label)}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <div
                className="tab-content my-container"
                style={{
                  overflow: 'auto',
                  height: window.innerHeight < 600 ? "65vh" : containerHeight,
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  maxWidth: '944px',
                }}
              >
                {tabContent[activeTab]}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Management3;

